import {
  DEFAULT_LOADER_TIMEOUT,
  FlowBaseUrlEnum,
} from '@constants/app.constants';
import {
  CmsDynamicFeatureCopy,
  CmsDynamicFlows,
  CmsDynamicKeysType,
  CmsDynamicSubFlows,
  CmsKeys,
  CmsSubKeys,
  ContentPageKeys,
  DYNAMIC, FeaturePageKeys,
  DynamicPageResponse,
} from '@constants/cms.constants';
import { DynamicSectionCopy } from '@models/cms/cms-dynamic/dynamic-page.model';
import { createSelector } from '@ngrx/store';
import { selectCmsFeatureState } from '@reducers';
import { convertToUpperCamelCase } from '@utils/data-utils';
import { selectAppFlowPathName, selectIsCheckInPath } from './router.selector';

export const selectAppCmsCopy = createSelector(selectCmsFeatureState, (cms) => {
  return cms[CmsKeys.App];
});

export const selectDocumentsCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.Documents];
  },
);

export const selectAccountCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.Account];
  },
);

export const selectNotFoundCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.NotFound];
  },
);

export const selectPaymentCopy = createSelector(
  selectCmsFeatureState,
  selectIsCheckInPath,
  (cms, isCheckInPath) => {
    if (isCheckInPath) {
      return cms[CmsKeys.Review];
    }
    return cms[CmsKeys.Payment];
  },
);

export const selectPageCacheFlowMap = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms.pageCacheFlowMap;
  },
);

export const selectBreadcrumbCopy = createSelector(selectAppCmsCopy, (app) => {
  return app?.breadcrumbs;
});

export const selectFlowCopy = createSelector(
  selectCmsFeatureState,
  selectAppFlowPathName,
  (cms, flowName) => {
    switch (flowName) {
      case FlowBaseUrlEnum.BOOK:
        return cms[CmsKeys.Book];
      case FlowBaseUrlEnum.CANCEL:
        return cms[CmsKeys.Cancel];
      case FlowBaseUrlEnum.CHECK_IN:
        return cms[CmsKeys.CheckIn];
      case FlowBaseUrlEnum.MODIFY:
        return cms[CmsKeys.Modify];
      case FlowBaseUrlEnum.PAY:
        return cms[CmsKeys.Pay];
      case FlowBaseUrlEnum.TRIPS:
        return cms[CmsKeys.Trips];
      case FlowBaseUrlEnum.UPGRADE:
        return cms[CmsKeys.Upgrade];
      default:
        return cms[CmsKeys.Book];
    }
  },
);

export const selectCancelCopy = createSelector(selectCmsFeatureState, (cms) => {
  return cms[CmsKeys.Cancel];
});

export const selectBagsCopy = createSelector(selectCmsFeatureState, (cms) => {
  return cms[CmsKeys.Bags];
});

export const selectPurchaseConfirmationCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.Confirmation];
  },
);

export const selectMealsCopy = createSelector(selectCmsFeatureState, (cms) => {
  return cms[CmsKeys.Meals];
});

export const selectSeatsCopy = createSelector(selectCmsFeatureState, (cms) => {
  return cms[CmsKeys.Seats];
});

export const selectBookCopy = createSelector(selectCmsFeatureState, (cms) => {
  return cms[CmsKeys.Book];
});

export const selectExtrasCopy = createSelector(selectCmsFeatureState, (cms) => {
  return cms[CmsKeys.Extras];
});

export const selectFindTripsCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.Trips];
  },
);

export const selectFlightsCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.Flights];
  },
);

export const selectFlightsFareAvailabilityRibbonCopy = createSelector(
  selectFlightsCopy,
  (flightsCopy) => {
    return flightsCopy?.[CmsSubKeys.fareAvailabilityRibbon];
  },
);

export const selectHomeCopy = createSelector(selectCmsFeatureState, (cms) => {
  return cms[CmsKeys.Home];
});

export const selectPassengerCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.Travelers];
  },
);

export const selectPersonalInfoCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.PersonalInfo];
  },
);

export const selectTravelPreferencesCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.TravelPreferences];
  },
);

export const selectContactInfoCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.ContactInfo];
  },
);

export const selectTravelDocumentsCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.TravelDocuments];
  },
);

export const selectPassengerTypesCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.App]?.passengerTypes;
  },
);

export const selectPurchaseCancellationCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.PurchaseCancellation];
  },
);

export const selectTripDetailsCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.TripDetails];
  },
);

export const selectReceiptCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.Receipt];
  },
);

export const selectFlightStatusCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.FlightStatus];
  },
);

export const selectDynamicCopy = createSelector(
  selectCmsFeatureState,
  (cms) => cms[DYNAMIC],
);

export const selectExperienceCopy = createSelector(selectDynamicCopy, (cms) => {
  return cms?.[CmsDynamicFlows.Experience];
});

export const selectExperienceSubNavCopy = createSelector(
  selectExperienceCopy,
  (cms) => {
    return cms?.subNav;
  },
);

export const selectWhereWeFlyCopy = createSelector(
  selectExperienceCopy,
  (cms) => {
    return cms?.WhereWeFly;
  },
);

export const selectSustainabilityExperienceCopy = createSelector(
  selectExperienceCopy,
  (cms) => {
    return cms?.Sustainability;
  },
);

export const selectMenusCopy = createSelector(selectDynamicCopy, (cms) => {
  return cms?.[CmsDynamicFlows.Menus];
});

export const selectDynamicCmsContentCopy = (path: string) =>
  createSelector(selectDynamicCopy, (dynamic) => {
    const pathWithoutFragments = path.split('#')[0]!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const pathWithoutQueryParams = pathWithoutFragments.split('?')[0]!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const paths = pathWithoutQueryParams.split('/').filter((p) => !!p);
    if (!paths.length || paths.length < 2 || !dynamic) return;

    paths.shift();
    let workingCopy:
      | CmsDynamicFeatureCopy
      | DynamicSectionCopy
      | CmsDynamicKeysType
      | null = dynamic[
      convertToUpperCamelCase(paths[0] ?? '')
    ] as DynamicSectionCopy | null;

    paths.shift();
    paths.forEach((p) => {
      workingCopy = workingCopy?.[convertToUpperCamelCase(p)] as
        | CmsDynamicFeatureCopy
        | DynamicSectionCopy
        | CmsDynamicKeysType
        | null;
    });

    // Should always be FeatureCopy or null by now
    return workingCopy as CmsDynamicFeatureCopy | null;
  });

export const selectPromoCopy = createSelector(selectDynamicCopy, (cms) => {
  return cms?.[CmsDynamicFlows.Promo];
});

export const selectSupportCopy = createSelector(selectDynamicCopy, (cms) => {
  return cms?.[CmsDynamicFlows.Support];
});

export const selectSupportSubNavCopy = createSelector(
  selectSupportCopy,
  (cms) => {
    return cms?.subNav;
  },
);

export const selectHelpCenterCopy = createSelector(selectSupportCopy, (cms) => {
  return cms?.HelpCenter;
});

export const selectOptionalFeesCopy = createSelector(
  selectSupportCopy,
  (cms) => {
    return cms?.OptionalFees;
  },
);

export const selectPreFlightCheckListCopy = createSelector(
  selectSupportCopy,
  (cms) => {
    return cms?.PreFlightChecklist;
  },
);

export const selectLegalCopy = createSelector(selectSupportCopy, (cms) => {
  return cms?.Legal;
});

export const selectCorporateCopy = createSelector(selectDynamicCopy, (cms) => {
  return cms?.[CmsDynamicFlows.Corporate];
});

export const selectCorporateSubNavCopy = createSelector(
  selectCorporateCopy,
  (cms) => {
    return cms?.subNav;
  },
);

export const selectAboutCopy = createSelector(selectCorporateCopy, (cms) => {
  return cms?.[CmsDynamicSubFlows.About];
});

export const selectMediaCopy = createSelector(selectCorporateCopy, (cms) => {
  return cms?.[CmsDynamicSubFlows.Media];
});

export const selectMediaLibraryCopy = createSelector(selectMediaCopy, (cms) => {
  return cms?.MediaLibrary;
});

export const selectInvestorsCopy = createSelector(
  selectCorporateCopy,
  (cms) => {
    return cms?.[CmsDynamicSubFlows.Investors];
  },
);

export const selectSustainabilityCopy = createSelector(
  selectAboutCopy,
  (cms) => {
    return cms?.Sustainability;
  },
);

export const selectFinancialCalendarCopy = createSelector(
  selectInvestorsCopy,
  (cms) => {
    return cms?.FinancialCalendar;
  },
);

export const selectShareholdersCopy = createSelector(
  selectInvestorsCopy,
  (cms) => {
    return cms?.Shareholders;
  },
);

export const selectAnalystCoverageCopy = createSelector(
  selectInvestorsCopy,
  (cms) => {
    return cms?.AnalystCoverage;
  },
);

export const selectBoardingPassCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[CmsKeys.BoardingPass];
  },
);

export const selectFareAvailabilityRibbonCopy = createSelector(
  selectFlightsCopy,
  (flightsCopy) => {
    return flightsCopy?.fareAvailabilityRibbon;
  },
);

export const selectAvailabilityFlightDetailsCopy = createSelector(
  selectFlightsCopy,
  (flightsCopy) => {
    return flightsCopy?.availabilityFlightDetail;
  },
);

export const selectFlightNewFareBrandCards = createSelector(
  selectFlightsCopy,
  (flightsCopy) => {
    return flightsCopy?.newFareBrandCards;
  },
);

export const selectUpsellPopupCopy = createSelector(
  selectFareAvailabilityRibbonCopy,
  selectFlightNewFareBrandCards,
  (fareAvailabilityRibbonCopy, flightNewFareBrandCards)  => {
    return {
      fareAvailabilityRibbonCopy, flightNewFareBrandCards
    }
  }
)

export const selectFlightFareInfoDisclaimer = createSelector(
  selectFlightsCopy,
  (flightsCopy) => {
    return {
      description: flightsCopy?.fareInfoDisclaimerDescription,
      title: flightsCopy?.fareInfoDisclaimerTitle,
    };
  },
);

export const selectLoaderTimeout = createSelector(
  selectCmsFeatureState,
  (cms) => {
    if (cms[CmsKeys.App]?.loader.displayTimeInMiliseconds) {
      return +cms[CmsKeys.App].loader.displayTimeInMiliseconds;
    }
    return DEFAULT_LOADER_TIMEOUT;
  },
);

export const selectFlashSales = createSelector(
  selectHomeCopy,
  (homeCopy) => homeCopy?.flashSales
)

export const selectCabnPromos = createSelector(
  selectFlightsCopy,
  (cms) => {
    return cms?.[CmsSubKeys.cabinPromo2];
  },
);

export const selectAnnounceBanners = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[ContentPageKeys.AnnounceBanners]?.announceBanners;
  },
);

export const selectPromoDeals = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[ContentPageKeys.Deals]?.promoDeals;
  },
);

export const selectFlashSalesUmbraco = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[ContentPageKeys.FlashSales]?.flashSales;
  },
);

export const selectSkybidFlightsCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[ContentPageKeys.Skybid];
  },
);

export const selectSkybidPaymentCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[ContentPageKeys.SkybidPayment];
  },
);

export const selectCheckInMessageBoxCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[ContentPageKeys.CheckInMessageBox];
  },
);

export const selectFareBrandCardsCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[ContentPageKeys.FareBrandCards];
  },
);

export const selectActiveDynamicPage = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms?.activeDynamicPage;
  },
);

export const selectDynamicPageContent = createSelector(
  selectCmsFeatureState,
  selectActiveDynamicPage,
  (cms, activePage) => {
    if (activePage) {
      return cms[activePage] as DynamicPageResponse;
    }
  },
);

export const selectDeeplinksCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.Deeplinks];
  },
);

export const selectSkybidMMBHomePageV1Copy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.SkybidMMBHomePageV1];
  },
);

export const selectSkybidMMBHomePageV2Copy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.SkybidMMBHomePageV2];
  },
);
export const selectSkybidMMBHomePageV3Copy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.SkybidMMBHomePageV3];
  },
);

export const selectSkybidMMBHomePageV4Copy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.SkybidMMBHomePageV4];
  },
);

export const selectCorporateCreditCardsCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.CorporateCreditCards];
  },
);

export const selectConfirmationAppDownloadBannerCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.AppDownloadBanner];
  },
);

export const selectSkybidMMBHomePageV6Copy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.SkybidMMBHomePageV6];
  },
);

export const selectYourTripsImprovementCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.YourTripsImprovement];
  },
);

export const selectPriceDisplayPayCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.PriceDisplayPayCopy];
  },
);

export const selectNavBarCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.NavbarCopy];
  },
);

export const selectClarifyingFinalStepOCI = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.ClarifyingFinalStepOCI];
  },
);
export const selectUpgradeNotAvailableCopy = createSelector(
  selectCmsFeatureState,
  (cms) => {
    return cms[FeaturePageKeys.UpgradeNotAvailable];
  },
);
